/**
 * Static capability catalog for direct microservice HTTP flows (not third-party-adapter).
 * Adapter-backed capabilities come from published Service Onboarding catalog API.
 */
import type { ServiceName } from '../domain/vendor';
import type { CapabilityOptionLike } from './adapterCapabilityCatalog';

function cf(name: string, description: string, required?: boolean) {
  return required === true ? { name, description, required: true } : { name, description };
}

export const LEGACY_CAPABILITY_CATALOG: CapabilityOptionLike[] = [
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.VERIFY',
    label: 'Verify Identity',
    endpointTemplate: '/validate',
    schema: 'identity',
    mappingTargets: [
      cf('verificationId', 'Maps vendor iv_verification_id / session id into canonical verificationId'),
      cf('status', 'IV wire status: SUCCESS | REJECTED | FAILED (and vendor-specific variants)'),
      cf('applicantId', 'Vendor applicant / dossier id when exposed as applicant_id'),
      cf('outcomeClass', 'Orchestrator outcomeClass when present (SUCCESS | SOFT_FAIL | HARD_STOP)'),
      cf('identityConfidence', 'Scalar OCR or match confidence when exposed (e.g. ocr_confidence_score)'),
      cf('livenessConfidence', 'From checks_result / vendor liveness payload when available'),
      cf('isDuplicate', 'Duplicate signal when vendor exposes it'),
      cf('matchedCid', 'Matched customer id from closest_customers / vendor'),
      cf('existingCcid', 'Resolved ccid for returning customer'),
      cf('ocrData', 'Structured OCR object (ocr_data) — map subfields or whole object'),
      cf('checksResult', 'checks_result / vendor checks blob'),
      cf('govIdCanonical', 'Registry or vendor gov id canonical string when separated from ocrData'),
      cf('biometricAnchorRef', 'When returned on wire (registry_cid_issuance.biometric_anchor_ref)'),
      cf('raw', 'Full customerResult / vendor JSON'),
    ],
  },
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.SDK_STATUS',
    label: 'SDK Status',
    endpointTemplate: '/sdk-verification/status/{verification_id}',
    schema: 'identity',
    mappingTargets: [
      cf('verificationId', 'iv_verification_id'),
      cf('status', 'Polling status (IDV_COMPLETED, FAILED, …)'),
      cf('vendor', 'SDK vendor id'),
      cf('verificationRoute', 'verification_route when present'),
      cf('reason', 'Machine failure reason'),
      cf('message', 'User-facing terminal message'),
      cf('raw', 'Full status JSON / envelope'),
    ],
  },
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.SDK_RESULTS',
    label: 'SDK Results',
    endpointTemplate: '/sdk-verification/results/{verification_id}',
    schema: 'identity',
    mappingTargets: [
      cf('verificationId', 'iv_verification_id'),
      cf('status', 'Final session status'),
      cf('vendor', 'SDK vendor id'),
      cf('ocrData', 'ocr_data extracted fields'),
      cf('checksResult', 'checks_result authenticity / liveness'),
      cf('documentType', 'document_type'),
      cf('identityConfidence', 'Optional scalar derived from checks'),
      cf('govIdCanonical', 'If issuer fields are flattened to a single canonical ref'),
      cf('raw', 'Full results JSON'),
    ],
  },
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.DEDUP',
    label: 'Deduplicate',
    endpointTemplate: '/deduplicate',
    schema: 'identity',
    mappingTargets: [
      cf('status', 'SUCCESS | REJECTED | FAILED from dedupe endpoint'),
      cf('isDuplicate', 'duplicate / isDuplicate boolean when exposed'),
      cf('matchedCid', 'Matched customer ccid from closest_customers[0]'),
      cf('existingCcid', 'Resolved ccid on success'),
      cf('message', 'Human-readable dedupe message'),
      cf('closestCustomers', 'closest_customers array when present'),
      cf('raw', 'Full DeduplicationResponseDto JSON'),
    ],
  },
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.ENRICH',
    label: 'Enrich Customer',
    endpointTemplate: '/enrich/{ccid}',
    schema: 'identity',
    mappingTargets: [
      cf('status', 'SUCCESS | FAILED'),
      cf('existingCcid', 'ccid on wire'),
      cf('enrichmentData', 'enrichment_data vendor/PDL blob'),
      cf('vendor', 'vendor label when returned'),
      cf('enrichmentSource', 'enrichment_source customer_record | vendor'),
      cf('message', 'Optional status message'),
      cf('govIdCanonical', 'Optional flattened canonical id when not inside enrichmentData'),
      cf('raw', 'Full CustomerEnrichResponseDto JSON'),
    ],
  },
  {
    serviceName: 'identity-verification',
    capability: 'IDENTITY.LOOKUP_CUSTOMER',
    label: 'Lookup Customer',
    endpointTemplate: '/lookup-customer',
    schema: 'identity',
    mappingTargets: [
      cf('outcomeClass', 'Envelope outcomeClass e.g. SUCCESS'),
      cf('matchedCid', 'ccid from customerResult.results[0] when found'),
      cf('existingCcid', 'Same ccid alternate path'),
      cf('customerResult', 'Full lookup results payload for nested JSONata'),
      cf('status', 'Derived status if vendor wraps differently'),
      cf('raw', 'Full POST /lookup-customer response JSON'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.BANK_CONNECT',
    label: 'Bank Connect',
    endpointTemplate: '/bank/connect',
    schema: 'income',
    mappingTargets: [
      cf('requestId', 'Aggregator / session request id for MFA and getTransactions (response field)'),
      cf('status', 'Typically MFA_REQUIRED (202) or CONNECTED (200)'),
      cf(
        'securityChallenges',
        'MFA challenge list when status is MFA_REQUIRED (provider response — not the MFA *request* body field securityResponses)',
      ),
      cf('sessionId', 'Income session id when echoed by provider'),
      cf('message', 'Human-readable status message'),
      cf('loginId', 'Present when connect completes without MFA (200)'),
      cf('applicationId', 'Echo from request when returned'),
      cf('ccid', 'Echo from request when returned'),
      cf('raw', 'Full provider JSON body'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.BANK_MFA',
    label: 'Bank MFA',
    endpointTemplate: '/bank/mfa',
    schema: 'income',
    mappingTargets: [
      cf('requestId', 'Aggregator request id'),
      cf('loginId', 'Login id when MFA completes (may be null until accounts step)'),
      cf('mfaRequired', 'Whether further MFA rounds are required'),
      cf('securityChallenges', 'Next MFA challenges when another round is required'),
      cf('status', 'e.g. MFA_COMPLETED'),
      cf('responseData', 'Nested aggregator payload when returned'),
      cf('message', 'Status message from provider'),
      cf('raw', 'Full provider JSON body'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.BANK_VERIFY',
    label: 'Bank Verify',
    endpointTemplate: '/bank/verify',
    schema: 'income',
    mappingTargets: [
      cf('verdict', 'PASS | REVIEW | FAIL (IncomeVerdict on IV wire)'),
      cf(
        'normalizedMonthlyIncome',
        'IV bank/verify customerResult uses dollars (same field as persisted OutputMetric)',
        true,
      ),
      cf('monthlyCents', 'Optional: only if vendor emits cents; map with JSONata (* 100) from normalizedMonthlyIncome'),
      cf('stabilityScore', '0–1 stability'),
      cf('volatilityIndex', '0–1 volatility'),
      cf('recurrenceConfidence', '0–1 recurrence'),
      cf('sessionId', 'Income session id'),
      cf('applicationId', 'Application id echo'),
      cf('failReasons', 'failReasons array when present'),
      cf('depositSummary', 'ingested/processed/excluded counts when present'),
      cf('raw', 'Full orchestrated / customerResult JSON'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.PAYSTUB_VERIFY',
    label: 'Paystub Verify',
    endpointTemplate: '/paystub/verify-multi',
    schema: 'income',
    mappingTargets: [
      cf('verdict', 'PASS | REVIEW | FAIL'),
      cf('status', 'Paystub pipeline status string'),
      cf('normalizedMonthlyIncome', 'Median monthly income (IV paystub verify)'),
      cf('monthlyCents', 'Optional cents form if vendor-only'),
      cf('stabilityScore', '0–1 from paystubOutcome'),
      cf('volatilityIndex', '0–1'),
      cf('recurrenceConfidence', '0–1'),
      cf('sessionId', 'Session id'),
      cf('primaryPayor', 'Employer / payor string'),
      cf('message', 'Outcome message'),
      cf('raw', 'Full INCOME_PAYSTUB_VERIFY payload'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.VERIFICATION_LATEST',
    label: 'Verification Latest',
    endpointTemplate: '/income/applications/{applicationId}/latest',
    schema: 'income',
    mappingTargets: [
      cf('verdict', 'Persisted IncomeVerdict'),
      cf('normalizedMonthlyIncome', 'From OutputMetric (IV GET latest — not monthlyCents field name)'),
      cf('monthlyCents', 'Optional: map from normalizedMonthlyIncome if consumers need cents'),
      cf('stabilityScore', 'From latest metric'),
      cf('volatilityIndex', 'From latest metric'),
      cf('recurrenceConfidence', 'From latest metric'),
      cf('sessionId', 'Income session id'),
      cf('applicationId', 'Application id'),
      cf('ccid', 'Customer id'),
      cf('incomeClass', 'e.g. CLASS_B'),
      cf('incomeType', 'e.g. UNSTRUCTURED'),
      cf('frequencyClassification', 'Pay frequency bucket'),
      cf('primaryPayor', 'Payor string'),
      cf('message', 'Human summary'),
      cf('raw', 'Full getLatestForApplication JSON'),
    ],
  },
  {
    serviceName: 'income-verification',
    capability: 'INCOME.PROBE_CLASS',
    label: 'Probe Class',
    endpointTemplate: '/income/probe-class',
    schema: 'income-probe',
    mappingTargets: [
      cf(
        'class',
        'Income class signal: orchestrator schema allows A|B|C; probe vendors may return PAYROLL/UNKNOWN — normalize in JSONata',
      ),
      cf('confidence', '0–1 classification confidence'),
      cf('rationale', 'Human-readable rationale'),
      cf('requestId', 'Optional vendor request id when returned beside result'),
      cf('raw', 'Full probe response when not class/confidence/rationale only'),
    ],
  },
  {
    serviceName: 'employer-verification',
    capability: 'EMPLOYER.PREFILL',
    label: 'Employer Prefill',
    endpointTemplate: '/verifications/prefill/{ccid}',
    schema: 'employer',
    mappingTargets: [
      cf('applicantName', 'Applicant full name (PrefillDataDto.applicant.name)'),
      cf('applicantPhone', 'Applicant phone'),
      cf('applicantHomeAddress', 'Applicant home address'),
      cf('employerName', 'Employer name'),
      cf('employerJobTitle', 'Job title'),
      cf('hasEmployment', 'Whether employment signals are present'),
      cf('employerCanonicalRef', 'Stable employer / place reference when available'),
      cf('raw', 'Raw provider or merged enrichment payload'),
    ],
  },
  {
    serviceName: 'employer-verification',
    capability: 'EMPLOYER.VERIFY',
    label: 'Employer Verify',
    endpointTemplate: '/verifications',
    schema: 'employer',
    mappingTargets: [
      cf('outcome', 'Verification outcome / status'),
      cf('employerCanonicalRef', 'Canonical employer reference'),
      cf('employerName', 'Verified employer name'),
      cf('raw', 'Raw provider response'),
    ],
  },
  {
    serviceName: 'decision-engine',
    capability: 'DE.PROVENIR_EVALUATE',
    label: 'Provenir Evaluate',
    endpointTemplate: '/mock/provenir/customer_snapshot',
    schema: 'decision-engine',
    adapterServiceKey: 'decision-engine',
    mappingTargets: [
      cf('xml', 'Provenir customer_snapshot XML body', true),
      cf('documentnumber', 'Exact documentnumber lookup key echoed from request/response'),
      cf('raw', 'Full mock JSON wrapper when present'),
    ],
    requestMappingTargets: [
      cf('identifier', 'Vendor lookup identifier passed by svc-de (maps to mock query documentnumber)', true),
      cf('documentNumber', 'Deprecated alias for identifier', false),
      cf('applicationId', 'Application id for correlation'),
      cf('correlationId', 'Optional correlation id'),
    ],
  },
] as CapabilityOptionLike[];

export function legacyCapabilitiesForService(serviceName: ServiceName): CapabilityOptionLike[] {
  return LEGACY_CAPABILITY_CATALOG.filter(c => c.serviceName === serviceName);
}
